'use client';

import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { getSectionId } from './section-id';
import { getSectionSpacing } from './section-spacing';

interface FaqCarouselBlockProps {
	block: Record<string, unknown>;
}

export function FaqCarouselBlock({ block }: FaqCarouselBlockProps) {
	const eyebrow = block.eyebrow as string | undefined;
	const heading = block.heading as string | undefined;
	const items = (block.items as { question?: string; answer?: string }[]) || [];
	const backgroundColor = (block.backgroundColor as string) || 'gray';
	const sectionId = getSectionId(block);
	const sectionSpacing = block.sectionSpacing as string | undefined;
	const panelClass = backgroundColor === 'white' ? 'bg-white' : 'bg-neutral-50';

	const [emblaRef, emblaApi] = useEmblaCarousel({
		align: 'start',
		dragFree: true,
		loop: false,
		containScroll: 'trimSnaps',
		slidesToScroll: 1,
		duration: 25,
	});

	const [canScrollPrev, setCanScrollPrev] = useState(false);
	const [canScrollNext, setCanScrollNext] = useState(false);

	const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
	const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

	const onSelect = useCallback(() => {
		if (!emblaApi) return;
		setCanScrollPrev(emblaApi.canScrollPrev());
		setCanScrollNext(emblaApi.canScrollNext());
	}, [emblaApi]);

	useEffect(() => {
		if (!emblaApi) return;
		onSelect();
		emblaApi.on('select', onSelect);
		emblaApi.on('reInit', onSelect);
		return () => {
			emblaApi.off('select', onSelect);
			emblaApi.off('reInit', onSelect);
		};
	}, [emblaApi, onSelect]);

	if (items.length === 0) return null;

	return (
		<section
			id={sectionId}
			className={getSectionSpacing(sectionSpacing, { hasHeader: Boolean(eyebrow || heading) })}
		>
			{/* Panel background runs full-bleed; content stays in the container. */}
			<div className={`${panelClass} py-16`}>
				<div className="container-page">
					<div className="flex items-end justify-between">
						<div>
							{eyebrow && (
								<p className="text-xs font-semibold uppercase tracking-widest text-brand-accent">
									{eyebrow}
								</p>
							)}
							{heading && (
								<h2 className="mt-1 text-2xl font-black uppercase tracking-tight text-neutral-900 md:text-3xl">
									{heading}
								</h2>
							)}
						</div>
						<div className="flex items-center gap-2">
							<button
								type="button"
								onClick={scrollPrev}
								disabled={!canScrollPrev}
								className="flex h-10 w-10 items-center justify-center rounded-full border border-neutral-300 text-neutral-700 transition-colors hover:border-neutral-900 hover:text-neutral-900 disabled:opacity-30"
								aria-label="Previous questions"
							>
								<ChevronLeft className="h-5 w-5" />
							</button>
							<button
								type="button"
								onClick={scrollNext}
								disabled={!canScrollNext}
								className="flex h-10 w-10 items-center justify-center rounded-full border border-neutral-300 text-neutral-700 transition-colors hover:border-neutral-900 hover:text-neutral-900 disabled:opacity-30"
								aria-label="Next questions"
							>
								<ChevronRight className="h-5 w-5" />
							</button>
						</div>
					</div>

					<div ref={emblaRef} className="mt-8 overflow-hidden cursor-grab active:cursor-grabbing">
						<div className="-ml-4 flex">
							{items.map((item) => (
								<div
									key={item.question}
									className="min-w-0 shrink-0 basis-[85%] pl-4 sm:basis-[45%] md:basis-1/3 lg:basis-1/4"
								>
									<div className="flex h-full flex-col rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
										{item.question && (
											<h3 className="text-sm font-bold uppercase tracking-wide text-neutral-900">
												{item.question}
											</h3>
										)}
										{item.answer && (
											<p className="mt-3 text-sm leading-relaxed text-neutral-600">{item.answer}</p>
										)}
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
